import { formatPeriodLong } from '@commissionsight/shared';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../api.js';
import { AUDIT_TABS, PageTabs } from '../components/PageTabs.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import { WorkspaceFilter } from '../components/WorkspaceFilter.js';
import { Button, Card, Field, inputClass } from '../components/ui.js';
import { downloadCsv, parseCsv, toCsv } from '../lib/csv.js';
import { formatCurrency } from '../lib/status.js';
import { useCarriers } from '../lib/useCarriers.js';

interface ReconcileResult {
  matched: number;
  censusTotal: number;
  paidTotal: number;
  unpaid: { memberExternalId: string; name: string | null; planName: string | null }[];
  unrostered: { memberExternalId: string; commissionAmount: number }[];
  medianPaidCommission: number;
  estimatedUnpaidAmount: number;
  unpaidTruncated?: boolean;
}
interface ReconcileResponse {
  period: string | null;
  carrierId: string;
  result: ReconcileResult | null;
}

/** Guess which CSV column holds the member id (and name / plan) from common headers. */
function guessColumn(headers: string[], patterns: RegExp[]): string {
  for (const p of patterns) {
    const hit = headers.find((h) => p.test(h));
    if (hit) return hit;
  }
  return '';
}

/**
 * Unpaid members — census reconciliation. Upload your active-member roster and we
 * compare it to who the carrier actually PAID, surfacing the members they never paid
 * you for and the estimated recoverable. Reconciled in memory; the roster is never
 * stored.
 */
export function UnpaidMembers() {
  const { baseUrl, token } = useAuth();
  const { carriers } = useCarriers();
  const [carrierId, setCarrierId] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [period, setPeriod] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState('');
  const [idCol, setIdCol] = useState('');
  const [nameCol, setNameCol] = useState('');
  const [planCol, setPlanCol] = useState('');
  const [resp, setResp] = useState<ReconcileResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rosterStatus, setRosterStatus] = useState<{
    count: number;
    uploadedAt: string | null;
  } | null>(null);
  const [savingRoster, setSavingRoster] = useState(false);

  // Load the SAVED roster status for the selected carrier/workspace (persisted rosters).
  useEffect(() => {
    if (!carrierId) {
      setRosterStatus(null);
      return;
    }
    let active = true;
    const qs = new URLSearchParams({ carrierId });
    if (workspaceId) qs.set('workspaceId', workspaceId);
    fetch(`${baseUrl.replace(/\/$/, '')}/census?${qs}`, {
      headers: { authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => active && b && setRosterStatus({ count: b.count, uploadedAt: b.uploadedAt }))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [baseUrl, token, carrierId, workspaceId]);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setResp(null);
    const text = await file.text();
    const { headers: h, rows: r } = parseCsv(text);
    setHeaders(h);
    setRows(r);
    setFileName(file.name);
    setIdCol(
      guessColumn(h, [
        /member.*id|subscriber.*id|\bmbi\b|\bhicn\b/i,
        /policy.*(number|id)/i,
        /\bid\b/i,
        /member/i,
      ]),
    );
    setNameCol(guessColumn(h, [/member.*name|full.*name|\bname\b/i]));
    setPlanCol(guessColumn(h, [/plan/i]));
  };

  const members = useMemo(
    () =>
      idCol
        ? rows
            .map((r) => ({
              memberExternalId: r[idCol] ?? '',
              name: nameCol ? (r[nameCol] ?? null) : null,
              planName: planCol ? (r[planCol] ?? null) : null,
            }))
            .filter((m) => m.memberExternalId.trim() !== '')
        : [],
    [rows, idCol, nameCol, planCol],
  );

  const reconcile = async () => {
    if (!carrierId || members.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${baseUrl.replace(/\/$/, '')}/reports/census-reconcile`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({
          carrierId,
          period: period || undefined,
          workspaceId: workspaceId || undefined,
          members,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => null);
        throw new Error(b?.title ?? `Reconcile failed (${res.status})`);
      }
      setResp(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reconcile');
    } finally {
      setBusy(false);
    }
  };

  // Save the uploaded roster to the server so future statements auto-reconcile.
  const saveRoster = async () => {
    if (!carrierId || members.length === 0) return;
    setSavingRoster(true);
    setError(null);
    try {
      const res = await fetch(`${baseUrl.replace(/\/$/, '')}/census`, {
        method: 'PUT',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({ carrierId, workspaceId: workspaceId || undefined, members }),
      });
      if (!res.ok) throw new Error('Failed to save roster');
      const b = (await res.json()) as { count: number };
      setRosterStatus({ count: b.count, uploadedAt: new Date().toISOString() });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save roster');
    } finally {
      setSavingRoster(false);
    }
  };

  // Reconcile the SAVED roster against a period — no re-upload.
  const reconcileSaved = async () => {
    if (!carrierId) return;
    setBusy(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ carrierId });
      if (period) qs.set('period', period);
      if (workspaceId) qs.set('workspaceId', workspaceId);
      const res = await fetch(`${baseUrl.replace(/\/$/, '')}/census/reconcile?${qs}`, {
        headers: { authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Reconcile failed (${res.status})`);
      setResp(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reconcile');
    } finally {
      setBusy(false);
    }
  };

  const result = resp?.result ?? null;
  const exportUnpaid = () => {
    if (!result) return;
    downloadCsv(
      `unpaid-members-${resp?.period ?? 'latest'}.csv`,
      toCsv(result.unpaid, [
        { key: 'memberExternalId', header: 'Member ID' },
        { key: 'name', header: 'Name' },
        { key: 'planName', header: 'Plan' },
      ]),
    );
  };

  return (
    <div className="space-y-6">
      <PageTabs tabs={AUDIT_TABS} />
      <div>
        <h1 className="text-2xl font-semibold">Unpaid members</h1>
        <p className="mt-1 max-w-2xl text-sm text-neutral-500">
          Upload your active-member roster and we compare it to who the carrier actually paid —
          surfacing the members they <strong>never paid you for</strong> and the estimated
          recoverable. Reconcile in memory once, or <strong>save the roster</strong> to
          auto-reconcile every future statement and track the unpaid trend.
        </p>
      </div>

      <Card>
        <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <Field label="Active-member roster (CSV)">
              <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-neutral-300 px-4 py-6 text-sm text-neutral-500 hover:border-emerald-400 dark:border-neutral-700">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])}
                />
                <span className="rounded-md bg-emerald-600 px-3 py-1.5 font-medium text-white">
                  Choose CSV
                </span>
                <span className="truncate">
                  {fileName
                    ? `${fileName} · ${rows.length} rows`
                    : 'A roster exported from your AMS / CRM'}
                </span>
              </label>
            </Field>
            {headers.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2">
                <Field label="Member ID column">
                  <select
                    className={inputClass}
                    value={idCol}
                    onChange={(e) => setIdCol(e.target.value)}
                  >
                    <option value="">—</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Name (optional)">
                  <select
                    className={inputClass}
                    value={nameCol}
                    onChange={(e) => setNameCol(e.target.value)}
                  >
                    <option value="">—</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Plan (optional)">
                  <select
                    className={inputClass}
                    value={planCol}
                    onChange={(e) => setPlanCol(e.target.value)}
                  >
                    <option value="">—</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            )}
          </div>
          <div className="space-y-3">
            <WorkspaceFilter value={workspaceId} onChange={setWorkspaceId} />
            <Field label="Carrier">
              <select
                className={inputClass}
                value={carrierId}
                onChange={(e) => setCarrierId(e.target.value)}
              >
                <option value="">Select a carrier…</option>
                {carriers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Statement period">
              <PeriodPicker value={period} onChange={setPeriod} placeholder="Latest period" />
            </Field>
            <Button
              onClick={reconcile}
              loading={busy}
              disabled={!carrierId || members.length === 0}
            >
              Reconcile {members.length > 0 ? `${members.length} members` : 'roster'}
            </Button>
          </div>
        </div>
        {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      </Card>

      {carrierId && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm">
              {rosterStatus && rosterStatus.count > 0 ? (
                <span>
                  <strong>{rosterStatus.count.toLocaleString()} members</strong> saved for this
                  carrier
                  {rosterStatus.uploadedAt
                    ? ` · updated ${new Date(rosterStatus.uploadedAt).toLocaleDateString()}`
                    : ''}
                  . Reconcile any period against it without re-uploading.
                </span>
              ) : (
                <span className="text-neutral-500">
                  No saved roster for this carrier yet — save the uploaded one to auto-reconcile
                  future statements.
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {members.length > 0 && (
                <Button variant="ghost" onClick={saveRoster} loading={savingRoster}>
                  {rosterStatus && rosterStatus.count > 0 ? 'Replace saved roster' : 'Save roster'}
                </Button>
              )}
              {rosterStatus && rosterStatus.count > 0 && (
                <Button onClick={reconcileSaved} loading={busy}>
                  Reconcile saved roster
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      {result && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Stat
              tone="rose"
              label="Estimated recoverable"
              value={formatCurrency(result.estimatedUnpaidAmount)}
              sub={`${result.unpaid.length} roster member${result.unpaid.length === 1 ? '' : 's'} the carrier didn't pay`}
            />
            <Stat
              tone="neutral"
              label="Roster matched"
              value={`${result.censusTotal > 0 ? Math.round((result.matched / result.censusTotal) * 100) : 0}%`}
              sub={`${result.matched} of ${result.censusTotal} paid · ${formatPeriodLong(resp?.period ?? null)}`}
            />
            <Stat
              tone="neutral"
              label="Basis · median paid"
              value={formatCurrency(result.medianPaidCommission)}
              sub="Per matched member (what similar members were paid)"
            />
          </div>

          <Card className="p-0">
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-2.5 dark:border-neutral-800">
              <span className="text-sm font-semibold">
                Unpaid members{result.unpaidTruncated ? ' (first 2,000)' : ''}
              </span>
              {result.unpaid.length > 0 && (
                <button
                  type="button"
                  onClick={exportUnpaid}
                  className="text-xs font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                >
                  Export CSV
                </button>
              )}
            </div>
            {result.unpaid.length === 0 ? (
              <p className="py-10 text-center text-sm text-neutral-500">
                Every roster member was paid this period. 🎉
              </p>
            ) : (
              <div className="grid grid-cols-[1.2fr_1.4fr_1fr] gap-3 px-5 py-2 text-xs font-medium text-neutral-500">
                <span>Member ID</span>
                <span>Name</span>
                <span>Plan</span>
              </div>
            )}
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {result.unpaid.slice(0, 500).map((m) => (
                <div
                  key={m.memberExternalId}
                  className="grid grid-cols-[1.2fr_1.4fr_1fr] gap-3 px-5 py-2 text-sm"
                >
                  <span className="truncate font-medium">{m.memberExternalId}</span>
                  <span className="truncate text-neutral-600 dark:text-neutral-300">
                    {m.name ?? '—'}
                  </span>
                  <span className="truncate text-neutral-500">{m.planName ?? '—'}</span>
                </div>
              ))}
            </div>
          </Card>

          {result.unrostered.length > 0 && (
            <p className="text-xs text-neutral-500">
              {result.unrostered.length} member{result.unrostered.length === 1 ? '' : 's'} the
              carrier paid that aren't in your roster — a stale roster, or members you may have
              forgotten you hold.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Stat({
  tone,
  label,
  value,
  sub,
}: {
  tone: 'rose' | 'neutral';
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <Card>
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</div>
      <div
        className={`mt-1 text-3xl font-semibold ${tone === 'rose' ? 'text-rose-600 dark:text-rose-400' : 'text-neutral-900 dark:text-neutral-100'}`}
      >
        {value}
      </div>
      <div className="mt-1 text-xs text-neutral-500">{sub}</div>
    </Card>
  );
}
